/* src/components/analytics/AnalyticsPage.js */
import React, { useState, useMemo, useCallback } from 'react';
import { useProfile } from '../../contexts/ProfileContext'; // Path to ProfileContext
import { useNavigate } from 'react-router-dom'; // <-- Import useNavigate

// Import sub-components
import ComparisonControls from './ComparisonControls';
import ComparisonSummary from './ComparisonSummary';
import ComparisonChart from './ComparisonChart';
import CommentModal from '../ui/CommentModal'; // Path to CommentModal
import {
    METRICS,
    PERIODS,
    calculateDateRanges,
    filterSessionsByDate,
    aggregateMetric,
    prepareChartData,
} from '../../utils/analyticsUtils'; // Path to analytics utilities

// --- UI Components for Loading/Error/Empty States ---
const LoadingSpinner = ({ message = "Loading analytics data..." }) => (
    <div className="flex flex-col justify-center items-center h-64 text-center text-gray-500">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mb-3"></div>
        <p>{message}</p>
    </div>
);

const ErrorDisplay = ({ message, navigate }) => (
    <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg border border-red-300 text-center" role="alert">
        <span className="font-medium">Error:</span> {message || 'An unknown error occurred.'}
        <button onClick={() => navigate('/dashboard')} className="ml-2 text-red-700 underline font-medium hover:text-red-900 text-xs">
             (Back to Dashboard)
        </button>
    </div>
);

const NoProfileMessage = ({ navigate }) => (
    <div className="p-6 max-w-4xl mx-auto text-center text-gray-500">
        No active profile selected or profile data is unavailable.
         <button onClick={() => navigate('/profile-selection')} className="ml-2 text-blue-600 hover:underline">
            Select Profile
        </button>
    </div>
);
// --- End UI Components ---

// Default settings for analytics view
const DEFAULT_METRIC = METRICS.AVG_FOCUS_LEVEL.key;
const DEFAULT_PERIOD = PERIODS.LAST_7_DAYS.key;

/**
 * AnalyticsPage Component
 * Displays performance analytics with comparisons over different periods.
 * Uses useNavigate for routing actions.
 * Made responsive using Tailwind CSS.
 */
const AnalyticsPage = () => {
    // --- Hooks ---
    const {
        activeProfile,
        addJournalEntry,
        addJournalTag,
        isLoading: isLoadingProfile,
        error: profileError
    } = useProfile();
    const navigate = useNavigate();

    // --- Local State ---
    const [selectedMetric, setSelectedMetric] = useState(DEFAULT_METRIC);
    const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_PERIOD);
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [snapshotSaveStatus, setSnapshotSaveStatus] = useState(null); // 'success', 'error', null

    // --- Memoized Analytics Data Calculation ---
    // Calculates the data needed for summary cards and chart based on selections.
    const analyticsData = useMemo(() => {
        const metricConfig = Object.values(METRICS).find(m => m.key === selectedMetric) || { key: selectedMetric, name: 'Metric', formatter: v => v };
        const periodConfig = Object.values(PERIODS).find(p => p.key === selectedPeriod) || { key: selectedPeriod, name: 'Period' };
        const defaultState = { currentPeriodSessions: [], previousPeriodSessions: [], currentValue: null, previousValue: null, chartData: [], metricInfo: metricConfig, periodInfo: periodConfig, currentPeriodLabel: 'Current Period', previousPeriodLabel: 'Previous Period', daysInPeriod: 7 };

        if (isLoadingProfile || profileError || !activeProfile?.sessions) {
            return defaultState;
        }

        const dateRanges = calculateDateRanges(selectedPeriod);
        if (!dateRanges) {
             return { ...defaultState, currentPeriodLabel: 'Invalid Period', previousPeriodLabel: 'Invalid Period' };
         }
         const options = { month: 'short', day: 'numeric' };
         const currentPeriodLabel = `${dateRanges.currentStart.toLocaleDateString(undefined, options)} - ${dateRanges.currentEnd.toLocaleDateString(undefined, options)}`;
         const previousPeriodLabel = `${dateRanges.previousStart.toLocaleDateString(undefined, options)} - ${dateRanges.previousEnd.toLocaleDateString(undefined, options)}`;

        const currentPeriodSessions = filterSessionsByDate(activeProfile.sessions, dateRanges.currentStart, dateRanges.currentEnd);
        const previousPeriodSessions = filterSessionsByDate(activeProfile.sessions, dateRanges.previousStart, dateRanges.previousEnd);

        const currentValue = aggregateMetric(currentPeriodSessions, selectedMetric);
        const previousValue = aggregateMetric(previousPeriodSessions, selectedMetric);

        let daysInPeriod;
        if (periodConfig.days) { daysInPeriod = periodConfig.days; }
        else if (selectedPeriod === 'thisMonth' || selectedPeriod === 'lastMonth') {
             const timeDiff = Math.abs(dateRanges.currentEnd.getTime() - dateRanges.currentStart.getTime());
             daysInPeriod = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
        } else { daysInPeriod = 7; }

        const rawChartData = prepareChartData(currentPeriodSessions, previousPeriodSessions, selectedMetric, dateRanges.currentStart, daysInPeriod);

        return { currentPeriodSessions, previousPeriodSessions, currentValue, previousValue, chartData: rawChartData, metricInfo: metricConfig, periodInfo: periodConfig, currentPeriodLabel, previousPeriodLabel, daysInPeriod };
    }, [activeProfile, selectedMetric, selectedPeriod, isLoadingProfile, profileError]); // Dependencies for recalculation


    // --- Event Handlers ---
    const handleMetricChange = useCallback((newMetricKey) => { setSelectedMetric(newMetricKey); }, []);
    const handlePeriodChange = useCallback((newPeriodKey) => { setSelectedPeriod(newPeriodKey); }, []);
    const handleOpenSnapshotModal = useCallback(() => {
        if (isLoadingProfile || profileError || !activeProfile || !analyticsData?.chartData?.length) {
            alert("Chart data is not available to snapshot."); return;
        }
        setIsCommentModalOpen(true); setSnapshotSaveStatus(null);
    }, [isLoadingProfile, profileError, activeProfile, analyticsData]);
    const handleCloseSnapshotModal = useCallback(() => { setIsCommentModalOpen(false); }, []);
    const handleSaveSnapshotWithComment = useCallback(async (comment = '', userTags = []) => {
        if (!activeProfile?.id || typeof addJournalEntry !== 'function' || typeof addJournalTag !== 'function' || !analyticsData) {
            alert("Could not save snapshot."); return;
        }
        const defaultTags = ['#snapshot', `#${analyticsData.metricInfo.key}`, `#${analyticsData.periodInfo.key}`];
        const allTagsRaw = [...new Set([...defaultTags, ...userTags])]; const finalSanitizedTags = [];
        await Promise.all(allTagsRaw.map(async (rawTag) => { const addedTag = await addJournalTag(activeProfile.id, rawTag); if (addedTag) finalSanitizedTags.push(addedTag); }));
        const uniqueFinalTags = [...new Set(finalSanitizedTags)];
        const chartDataForSave = (analyticsData.chartData || []).map(point => ({ ...point, date: point.date instanceof Date ? point.date.toISOString() : (typeof point.date === 'string' ? point.date : null) })).filter(point => point.date !== null);
        const snapshotData = { type: 'chart', metricKey: analyticsData.metricInfo.key, metricName: analyticsData.metricInfo.name, periodKey: analyticsData.periodInfo.key, periodName: analyticsData.periodInfo.name, currentValue: analyticsData.currentValue, previousValue: analyticsData.previousValue, chartData: chartDataForSave, currentPeriodLabel: analyticsData.currentPeriodLabel, previousPeriodLabel: analyticsData.previousPeriodLabel, snapshotTimestamp: new Date().toISOString() };
        const journalEntryData = { type: 'chart_snapshot', snapshotData: snapshotData, tags: uniqueFinalTags, ...(comment.trim() && { content: comment.trim() }) };
        try { const success = await addJournalEntry(activeProfile.id, journalEntryData); if (success) { setSnapshotSaveStatus('success'); handleCloseSnapshotModal(); } else { throw new Error("addJournalEntry returned falsy."); } }
        catch (error) { console.error("Error adding chart snapshot to journal:", error); setSnapshotSaveStatus('error'); alert("Failed to save chart snapshot."); handleCloseSnapshotModal(); }
        finally { setTimeout(() => setSnapshotSaveStatus(null), 3000); }
    }, [activeProfile, addJournalEntry, addJournalTag, analyticsData, handleCloseSnapshotModal]);
    // --- End Handlers ---


    // --- Render Logic ---

    // 1. Handle Initial Loading State
    if (isLoadingProfile && !activeProfile) {
        return ( <div className="p-6 max-w-4xl mx-auto text-center"> <LoadingSpinner /> </div> );
    }

    // 2. Handle Profile Loading Error State
    if (profileError) {
        return ( <div className="p-6 max-w-4xl mx-auto text-center"> <ErrorDisplay message={profileError} navigate={navigate} /> </div> );
    }

    // 3. Handle No Profile Selected State
    if (!activeProfile) {
        return <NoProfileMessage navigate={navigate} />;
    }

    // 4. Render Analytics Content
    return (
        // Main container with responsive padding
        <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
            {/* Max-width container */}
            <div className="max-w-4xl mx-auto">
                {/* Page Header - Responsive layout */}
                <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6">
                    {/* Title - Responsive text size */}
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-2 sm:mb-0 text-center sm:text-left">
                        Analytics for {activeProfile.username || 'Profile'}
                    </h1>
                    {/* Back Button - Consistent styling */}
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1 self-center sm:self-auto" // Center on mobile
                    >
                        ← Back to Dashboard
                    </button>
                </div>

                {/* Controls - Component already handles responsive stacking */}
                <ComparisonControls
                    selectedMetric={selectedMetric} onMetricChange={handleMetricChange}
                    selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange}
                />

                {/* Summary Cards - Component handles responsive grid */}
                <ComparisonSummary
                    metricName={analyticsData.metricInfo?.name || 'Metric'}
                    currentValue={analyticsData.currentValue}
                    previousValue={analyticsData.previousValue}
                    formatter={analyticsData.metricInfo?.formatter}
                />

                {/* Chart Area - Responsive padding */}
                <div className="bg-white p-3 sm:p-4 rounded-lg border shadow-sm mb-4">
                    {/* Loading state specifically for chart data (if needed) */}
                    {isLoadingProfile && analyticsData.chartData.length === 0 ? (
                        <LoadingSpinner message="Loading chart data..." />
                    ) : (
                         <>
                             {/* Chart Component */}
                             <ComparisonChart
                                chartData={analyticsData.chartData}
                                metricName={analyticsData.metricInfo?.name || 'Metric'}
                                selectedMetricKey={analyticsData.metricInfo?.key || selectedMetric}
                             />
                             {/* Snapshot Button - Responsive styling */}
                             <div className="mt-4 pt-3 border-t border-gray-200 text-center sm:text-right">
                                <button
                                    onClick={handleOpenSnapshotModal}
                                    disabled={!analyticsData.chartData?.length || snapshotSaveStatus === 'success'}
                                    // Responsive button styling
                                    className={`inline-flex items-center px-4 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 w-full sm:w-auto justify-center ${
                                        snapshotSaveStatus === 'success' ? 'bg-green-600 focus:ring-green-500 cursor-default' :
                                        snapshotSaveStatus === 'error' ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' :
                                        'bg-teal-600 hover:bg-teal-700 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed'
                                    }`}
                                    title={snapshotSaveStatus === 'success' ? "Snapshot Saved!" : "Save chart view to journal"}
                                >
                                     {/* Icon */}
                                     <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"> <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" /> </svg>
                                     {/* Text */}
                                    {snapshotSaveStatus === 'success' ? 'Saved ✓' : snapshotSaveStatus === 'error' ? 'Save Failed' : 'Snapshot to Journal'}
                                </button>
                            </div>
                         </>
                    )}
                </div>

            </div> {/* End max-width container */}

            {/* Comment/Tag Modal (Should be responsive itself) */}
            <CommentModal
                isOpen={isCommentModalOpen}
                onClose={handleCloseSnapshotModal}
                onSave={handleSaveSnapshotWithComment}
                availableTags={activeProfile?.journalTags || []}
                title="Add Note & Tags to Snapshot"
            />

        </div> /* End page wrapper */
    );
};

export default AnalyticsPage;